import { SearchFilterEnum } from 'api/actor';
import { ReferentialKey } from 'api/referential';
import { ComponentKey } from 'enums/componentKey';
import { PersonalInformationFieldNames } from 'features/new-actor/enums/personalInformationFieldNames';
import { LegalEntityFormFieldNames } from 'features/new-actor/enums/legalEntityFormFieldNames';
import { InputsConfig } from 'types/form/inputsConfig';
import { MinimalBorrowerFormFieldNames } from './minimalBorrowerForm';

// Visible field names for natural person (excluding hidden fields like id, borrowerId, surveyAnswers)
type MinimalNaturalPersonVisibleFields =
  | PersonalInformationFieldNames.Civility
  | PersonalInformationFieldNames.LastName
  | PersonalInformationFieldNames.FirstName
  | PersonalInformationFieldNames.Email
  | PersonalInformationFieldNames.BirthDate
  | typeof MinimalBorrowerFormFieldNames.AddressSearch
  | typeof MinimalBorrowerFormFieldNames.AdditionalAddress
  | typeof MinimalBorrowerFormFieldNames.ExistingOutstanding;

// Visible field names for legal entity
type MinimalLegalEntityVisibleFields =
  | LegalEntityFormFieldNames.CompanyName
  | PersonalInformationFieldNames.Email;

// Inputs config for minimal natural person form
export const minimalNaturalPersonInputsConfig: InputsConfig<MinimalNaturalPersonVisibleFields> = {
  [PersonalInformationFieldNames.Civility]: {
    componentKey: ComponentKey.ReferentialDropdown,
    referentialKey: ReferentialKey.Civility,
    name: PersonalInformationFieldNames.Civility,
    required: true,
    label: 'ACTOR_FORM.PERSONAL_INFOS.LABELS.CIVILITY'
  },
  [PersonalInformationFieldNames.LastName]: {
    componentKey: ComponentKey.InputText,
    name: PersonalInformationFieldNames.LastName,
    type: 'text',
    required: true,
    label: 'ACTOR_FORM.PERSONAL_INFOS.LABELS.LAST_NAME',
    placeholder: 'ACTOR_FORM.PERSONAL_INFOS.PLACEHOLDERS.LAST_NAME'
  },
  [PersonalInformationFieldNames.FirstName]: {
    componentKey: ComponentKey.InputText,
    name: PersonalInformationFieldNames.FirstName,
    type: 'text',
    required: true,
    label: 'ACTOR_FORM.PERSONAL_INFOS.LABELS.FIRST_NAME',
    placeholder: 'ACTOR_FORM.PERSONAL_INFOS.PLACEHOLDERS.FIRST_NAME'
  },
  [PersonalInformationFieldNames.Email]: {
    componentKey: ComponentKey.InputText,
    name: PersonalInformationFieldNames.Email,
    type: 'email',
    required: true,
    label: 'ACTOR_FORM.PERSONAL_INFOS.LABELS.EMAIL',
    placeholder: 'ACTOR_FORM.PERSONAL_INFOS.PLACEHOLDERS.EMAIL',
    existenceCheck: {
      enabled: true,
      type: 'email',
      searchFilter: SearchFilterEnum.Email,
      debounceMs: 800,
      errorMessage: 'Cette adresse email est déjà utilisée'
    }
  },
  [PersonalInformationFieldNames.BirthDate]: {
    componentKey: ComponentKey.InputText,
    name: PersonalInformationFieldNames.BirthDate,
    type: 'date',
    required: true,
    label: 'ACTOR_FORM.PERSONAL_INFOS.LABELS.DATE_OF_BIRTH',
    placeholder: 'ACTOR_FORM.PERSONAL_INFOS.PLACEHOLDERS.DATE_OF_BIRTH'
  },
  [MinimalBorrowerFormFieldNames.AddressSearch]: {
    componentKey: ComponentKey.GoogleLocation,
    name: MinimalBorrowerFormFieldNames.AddressSearch,
    label: 'ACTOR_FORM.ADDRESS.LABELS.ADDRESS',
    placeholder: 'ACTOR_FORM.ADDRESS.PLACEHOLDERS.ADDRESS',
    required: true,
    fieldPrefix: 'address'
  },
  [MinimalBorrowerFormFieldNames.AdditionalAddress]: {
    componentKey: ComponentKey.InputText,
    name: MinimalBorrowerFormFieldNames.AdditionalAddress,
    type: 'text',
    required: false,
    label: 'ACTOR_FORM.ADDRESS.LABELS.ADDITIONAL_ADDRESS',
    placeholder: 'ACTOR_FORM.ADDRESS.PLACEHOLDERS.ADDITIONAL_ADDRESS'
  },
  [MinimalBorrowerFormFieldNames.ExistingOutstanding]: {
    componentKey: ComponentKey.InputWithUnit,
    name: MinimalBorrowerFormFieldNames.ExistingOutstanding,
    type: 'number',
    label: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.LABELS.EXISTING_OUTSTANDING',
    labelTooltip: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.TOOLTIPS.EXISTING_OUTSTANDING',
    placeholder: 'CONTRACTUALIZATION.SPECIAL_CONDITIONS.PLACEHOLDERS.EXISTING_OUTSTANDING',
    unit: '€'
  },
};

// Inputs config for minimal legal entity form
export const minimalLegalEntityInputsConfig: InputsConfig<MinimalLegalEntityVisibleFields> = {
  [LegalEntityFormFieldNames.CompanyName]: {
    componentKey: ComponentKey.InputText,
    name: LegalEntityFormFieldNames.CompanyName,
    type: 'text',
    required: true,
    label: 'ACTOR_FORM.LEGAL_ENTITY.LABELS.COMPANY_NAME',
    placeholder: 'ACTOR_FORM.LEGAL_ENTITY.PLACEHOLDERS.COMPANY_NAME'
  },
  [PersonalInformationFieldNames.Email]: {
    componentKey: ComponentKey.InputText,
    name: PersonalInformationFieldNames.Email,
    type: 'email',
    required: true,
    label: 'ACTOR_FORM.PERSONAL_INFOS.LABELS.EMAIL',
    placeholder: 'ACTOR_FORM.PERSONAL_INFOS.PLACEHOLDERS.EMAIL',
    existenceCheck: {
      enabled: true,
      type: 'email',
      searchFilter: SearchFilterEnum.Email,
      debounceMs: 800,
      errorMessage: 'Cette adresse email est déjà utilisée'
    }
  }
};
